import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Star, CheckCircle, Send, MessageSquare, Loader2 } from 'lucide-react';
import { CJMSClient } from '../../api/cjmsApi';
import { feedbackBus } from '../../services/feedbackBus';

interface ReviewRequesterProps {
    client: CJMSClient;
}

const PLATFORMS = [
    { id: 'google', label: 'Google Reviews' },
    { id: 'facebook', label: 'Facebook' },
    { id: 'linkedin', label: 'LinkedIn Recommendations' },
    { id: 'trustpilot', label: 'Trustpilot' },
];

export const ReviewRequester: React.FC<ReviewRequesterProps> = ({ client }) => {
    const isEligible = client.currentStage === 'Advocacy' || client.currentStage === 'Growth';
    const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
    const [customMessage, setCustomMessage] = useState('');
    const [status, setStatus] = useState<'idle' | 'sending' | 'sent'>('idle');

    if (!isEligible) return null;

    const handleSend = async () => {
        setStatus('sending');
        await new Promise(r => setTimeout(r, 800));
        feedbackBus.emit('activity_completed', {
            activityLabel: `Review request sent via ${selectedPlatform}`,
            clientEmail: client.email,
        });
        setStatus('sent');
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
        >
            <div className="flex items-center gap-2 p-4 border-b border-slate-100">
                <Star className="w-4 h-4 text-yellow-500" />
                <h3 className="text-sm font-bold text-slate-800">Review Requester</h3>
            </div>
            <div className="p-4 space-y-4">
                <div className="flex items-center gap-2 p-2 bg-yellow-50 rounded-lg border border-yellow-100">
                    <MessageSquare className="w-3 h-3 text-yellow-600" />
                    <p className="text-[10px] text-yellow-700">Ask this client for a testimonial or review</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    {PLATFORMS.map(platform => (
                        <button
                            key={platform.id}
                            onClick={() => setSelectedPlatform(platform.id)}
                            className={`p-3 rounded-lg border text-left transition-all ${
                                selectedPlatform === platform.id
                                    ? 'border-yellow-400 bg-yellow-50'
                                    : 'border-slate-100 bg-white hover:border-yellow-200'
                            }`}
                        >
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-semibold text-slate-700">{platform.label}</span>
                                {selectedPlatform === platform.id && (
                                    <CheckCircle className="w-3 h-3 text-yellow-500 ml-auto" />
                                )}
                            </div>
                        </button>
                    ))}
                </div>
                {selectedPlatform && (
                    <div className="space-y-3">
                        <div>
                            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Custom Message</label>
                            <textarea
                                value={customMessage}
                                onChange={e => setCustomMessage(e.target.value)}
                                placeholder={`Hi ${client.name}, we'd love your feedback on ${PLATFORMS.find(p => p.id === selectedPlatform)?.label}...`}
                                className="w-full mt-1 px-3 py-2 text-xs border border-slate-200 rounded-lg resize-none h-20 focus:ring-1 focus:ring-yellow-400 outline-none"
                            />
                        </div>
                        <div className="flex justify-end">
                            <button
                                onClick={handleSend}
                                disabled={status === 'sending'}
                                className="px-4 py-1.5 text-[11px] font-medium bg-yellow-500 text-white rounded-md hover:bg-yellow-600 transition-colors disabled:opacity-50 flex items-center gap-1"
                            >
                                {status === 'sending' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                                Send Request
                            </button>
                        </div>
                    </div>
                )}
                {status === 'sent' && (
                    <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg border border-green-100">
                        <CheckCircle className="w-3 h-3 text-green-500" />
                        <span className="text-[10px] text-green-700 font-medium">Review request sent successfully!</span>
                    </div>
                )}
            </div>
        </motion.div>
    );
};
